import React, { useState } from 'react';
import { Plug, ChevronDown, ChevronRight, Loader2, CheckCircle, XCircle } from 'lucide-react';

const MCPToolProgress = ({ toolName, serverName, status, input, result, error, isLatest }) => {
  const [isExpanded, setIsExpanded] = useState(isLatest);

  const getStatusIcon = () => {
    switch (status) {
      case 'starting':
        return <Loader2 className="w-4 h-4 animate-spin text-purple-500 dark:text-purple-400" />;
      case 'executing':
        return <Loader2 className="w-4 h-4 animate-spin text-purple-500 dark:text-purple-400" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500 dark:text-red-400" />;
      default:
        return <Plug className="w-4 h-4 text-purple-500 dark:text-purple-400" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'starting':
        return 'Starting tool execution';
      case 'executing':
        return 'Executing tool';
      case 'completed':
        return 'Tool completed';
      case 'error':
        return 'Tool execution failed';
      default:
        return 'Tool execution';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'starting':
        return 'border-purple-200 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20';
      case 'executing':
        return 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20';
      case 'completed':
        return 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20';
      case 'error':
        return 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20';
      default:
        return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30';
    }
  };

  return (
    <div className={`rounded-lg border ${getStatusColor()} p-3 my-2`}>
      <div
        className="flex items-center gap-2 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        )}
        {getStatusIcon()}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
              {toolName}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              via {serverName}
            </span>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
            {getStatusText()}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-3 space-y-2 pl-6">
          {input && Object.keys(input).length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Input:</div>
              <div className="bg-white dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-600 p-2">
                <pre className="text-xs text-gray-700 dark:text-gray-100 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(input, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {result && (
            <div>
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Result:</div>
              <div className="bg-white dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-600 p-2">
                <pre className="text-xs text-gray-700 dark:text-gray-100 overflow-x-auto whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                  {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {error && (
            <div>
              <div className="text-xs font-medium text-red-700 dark:text-red-400 mb-1">Error:</div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-700 p-2">
                <pre className="text-xs text-red-800 dark:text-red-300 overflow-x-auto whitespace-pre-wrap break-words">
                  {error}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MCPToolProgress;